import { useCallback, useEffect, useState } from "react";

export type Theme = "light" | "dark";
const KEY = "zotula.theme";

function systemTheme(): Theme {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function current(): Theme {
  const set = document.documentElement.dataset.theme;
  return set === "light" || set === "dark" ? set : systemTheme();
}

/** The phone browser's bar takes the page colour (--bg) of the theme now in effect. */
function syncBrowserBar() {
  const bg = getComputedStyle(document.documentElement).getPropertyValue("--bg").trim();
  if (!bg) return;
  document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => meta.setAttribute("content", bg));
}

/** The theme in effect and a toggle. With no saved choice the page follows the OS. */
export function useTheme(): { theme: Theme; toggle: () => void } {
  const [theme, setTheme] = useState<Theme>(current);

  useEffect(() => {
    const media = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!media) return;
    const onChange = () => setTheme(current());
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const toggle = useCallback(() => {
    const next: Theme = current() === "dark" ? "light" : "dark";
    document.documentElement.dataset.theme = next;
    syncBrowserBar();
    try {
      localStorage.setItem(KEY, next);
    } catch {
      // The choice then lasts until reload.
    }
    setTheme(next);
  }, []);

  return { theme, toggle };
}
